import { getApiErrorMessage } from '@shared/utils/errorHandling';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { InvitationService } from '../services/invitation.service';
import { CreateInvitationRequest, Invitation, InvitationStatus } from '../types/invitation';

export type AdminInvitationFilter = InvitationStatus | 'all';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 600;

interface Query {
  search: string;
  status: AdminInvitationFilter;
  page: number;
}

export const useAdminInvitations = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // What is typed, which updates on every keystroke, and what is asked for,
  // which lags behind it. Keeping the request in a single object means one
  // change produces exactly one request: moving the search back to page one
  // used to fire a second call with the previous term.
  const [search, setSearchValue] = useState('');
  const [query, setQuery] = useState<Query>({ search: '', status: 'all', page: 1 });

  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [countsByStatus, setCountsByStatus] = useState<Record<string, number>>({});

  useEffect(() => {
    if (search === query.search) return;

    const timer = setTimeout(() => {
      setQuery((current) => ({ ...current, search, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, query.search]);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await InvitationService.getAdminInvitations({
        status: query.status === 'all' ? undefined : query.status,
        search: query.search.trim() || undefined,
        page: query.page,
        limit: PAGE_SIZE,
      });

      setInvitations(result.invitations);
      setTotal(result.total);
      setPageCount(result.pageCount);
      setCountsByStatus(result.countsByStatus ?? {});

      // The server clamps a page past the end; follow it so the controls agree.
      if (result.currentPage !== query.page) {
        setQuery((current) => ({ ...current, page: result.currentPage }));
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to load admin invitations', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const createInvitation = async (data: CreateInvitationRequest) => {
    setLoading(true);
    setError(null);

    try {
      await InvitationService.createAdminInvitation(data);
      toast.success(`Invitation sent to ${data.email}`);
      await fetchInvitations(); // Refresh the list
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to send admin invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteInvitation = async (invitationId: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await InvitationService.deleteInvitation(invitationId, true); // true for admin invitation
      toast.success(
        result?.purgedUser
          ? 'Invitation and the unfinished account were deleted. This email can be invited again.'
          : 'Invitation deleted successfully'
      );
      // Refetched rather than spliced: removing a row pulls the next one up
      // from the following page, and the counts change too.
      await fetchInvitations();
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to delete admin invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendInvitation = async (invitationId: string) => {
    setLoading(true);
    setError(null);

    try {
      const updatedInvitation = await InvitationService.resendInvitation(invitationId, true); // true for admin invitation
      toast.success('Invitation resent successfully');

      setInvitations((current) =>
        current.map((inv) => (inv.id === invitationId ? updatedInvitation : inv))
      );
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      console.error('Failed to resend admin invitation', err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = (invitationId: string) => {
    const invitationLink = `${window.location.origin}/invitation?id=${invitationId}`;
    navigator.clipboard.writeText(invitationLink)
      .then(() => {
        toast.success('Invitation link copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy invitation link', err);
        toast.error('Failed to copy invitation link');
      });
  };

  // Changing what is being looked at should always land on the first page.
  const setStatusFilter = (status: AdminInvitationFilter) => {
    setQuery((current) => ({ ...current, status, page: 1 }));
  };

  const setPage = (page: number) => {
    setQuery((current) => ({ ...current, page }));
  };

  /** True while the typed text has not reached the server yet. */
  const isSearchPending = search !== query.search;

  const totalForFilter = (filter: AdminInvitationFilter): number =>
    filter === 'all'
      ? Object.values(countsByStatus).reduce((sum, count) => sum + count, 0)
      : countsByStatus[filter] ?? 0;

  return {
    invitations,
    loading,
    error,
    fetchInvitations,
    createInvitation,
    deleteInvitation,
    resendInvitation,
    copyInvitationLink,
    search,
    setSearch: setSearchValue,
    isSearchPending,
    statusFilter: query.status,
    setStatusFilter,
    totalForFilter,
    page: query.page,
    setPage,
    pageCount,
    pageSize: PAGE_SIZE,
    total,
  };
};
